import {useFormikContext} from 'formik';
import {useCallback, useEffect, useRef} from 'react';

import SelectOutlet from '../../../../../../components/selectOutlet/index';

import {ConditionalField} from '../../components/CondiitonalField';
import DocumentImageUploader from '../../components/DocumentImageUploader';

import {getDocumentsForJobType} from '../../utils/docs';

import {IBankDocsGeneralForm, IPersonFormData, JobTypes} from '../LoanDocsPage';

interface AddExtraDocsProps {
  loanDocuments: ILoanDocumentResponse[];
  fieldPrefix: keyof IBankDocsGeneralForm;
  loanRequestGuid: string;
  loanRuleGuid: string;
  onAllImagesUploaded?: (isComplete: boolean) => void;
}

// Check if fieldPrefix belongs to a person form
const isPersonPrefix = (
  prefix: keyof IBankDocsGeneralForm,
): prefix is 'self' | 'Guarantor-0' | 'Guarantor-1' => {
  return prefix !== 'global';
};

const AddExtraDocs = ({
  loanDocuments,
  fieldPrefix,
  loanRequestGuid,
  loanRuleGuid,
  onAllImagesUploaded,
}: AddExtraDocsProps) => {
  const {values, setFieldValue} = useFormikContext<IBankDocsGeneralForm>();

  // Prevent rendering for global section
  if (!isPersonPrefix(fieldPrefix)) {
    return null;
  }

  const personData = values[fieldPrefix] as IPersonFormData;

  const job_type = personData?.job_type;

  // Prevent unnecessary formik updates
  const previousAllCompleteRef = useRef<boolean | null>(null);

  const jobOptions = [
    {label: 'انتخاب کنید', value: 0},
    {label: 'کارمند', value: 1},
    {label: 'شغل آزاد', value: 2},
    {label: 'مشاغل خاص', value: 3},
  ];

  // Get backend matched documents based on:
  // EXTRA-jobLetter-anything-userIndex-title
  const filteredDocs = getDocumentsForJobType(
    loanDocuments,
    job_type as JobTypes,
    fieldPrefix,
  );

  // Check all extra documents uploaded
  const areAllImagesUploaded = useCallback((): boolean => {
    if (filteredDocs.length === 0) {
      return true;
    }

    const personData = values[fieldPrefix] as IPersonFormData;

    if (!personData) {
      return false;
    }

    return filteredDocs.every((_, index) => {
      const fieldName = `extra_image_${index}`;

      const fieldValue = personData[fieldName as keyof IPersonFormData];

      return (
        fieldValue !== null &&
        fieldValue !== undefined &&
        (fieldValue instanceof File || typeof fieldValue === 'string')
      );
    });
  }, [filteredDocs, values, fieldPrefix]);

  const allComplete = areAllImagesUploaded();

  // Sync upload completion state to formik
  useEffect(() => {
    if (previousAllCompleteRef.current !== allComplete) {
      previousAllCompleteRef.current = allComplete;

      setFieldValue(`${fieldPrefix}._allJobImagesUploaded`, allComplete, false);

      if (onAllImagesUploaded) {
        onAllImagesUploaded(allComplete);
      }
    }
  }, [allComplete, fieldPrefix, setFieldValue, onAllImagesUploaded]);

  // Reset upload completion when job type changes
  useEffect(() => {
    previousAllCompleteRef.current = null;

    setFieldValue(`${fieldPrefix}._allJobImagesUploaded`, false, false);
  }, [job_type, fieldPrefix, setFieldValue]);

  return (
    <div className="mb-4 text-sm">
      <section className="space-y-4">
        <div>
          <p className="mb-2 text-gray-700 text-md">
            شغل متقاضی <span className="text-red-700">*</span>
          </p>

          <SelectOutlet
            name={`${fieldPrefix}.job_type`}
            hasEmptyData={false}
            label=""
            placeholder="انتخاب کنید"
            value={job_type || ''}
            options={jobOptions}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              setFieldValue(
                `${fieldPrefix}.job_type`,
                Number(e.target.value),
                false,
              );

              Object.keys(personData).forEach(key => {
                if (key.startsWith('extra_image_')) {
                  (personData as any)[key] = null;
                }
              });
            }}
          />
        </div>

        <ConditionalField show={Boolean(job_type)}>
          <div className="space-y-4">
            {filteredDocs.map((doc, index) => {
              // Example:
              // EXTRA-FS-anything-0-title
              // title starts after index 4
              const titleParts = doc.title.split('-');

              const readableTitle = titleParts.slice(4).join('-') || 'مدرک';

              return (
                <div key={doc.guid}>
                  <DocumentImageUploader
                    title={readableTitle}
                    loanRequestGuid={loanRequestGuid}
                    loanRuleGuid={loanRuleGuid}
                    loanDocumentGuid={doc.guid}
                    personPrefix={fieldPrefix}
                    fieldName={`extra_image_${index}`}
                  />
                </div>
              );
            })}
          </div>
        </ConditionalField>
      </section>
    </div>
  );
};

export default AddExtraDocs;
